/* Highlights section.
   - Video tiles play only while near the viewport and pause when they leave,
     so tiles below the fold or scrolled off a slider don't download or decode.
   - Drives the arrows of the mobile "slider" layout: one tile per click,
     arrows disabled at either end, and hidden when every tile already fits.
     The grid layout and desktop have no controls. */
(() => {
  if (customElements.get('highlights-section')) return;

  class HighlightsSection extends HTMLElement {
    connectedCallback() {
      this.observeVideos();

      this.track = this.querySelector('[data-highlights-track]');
      this.controls = this.querySelector('[data-highlights-controls]');
      if (!this.track || !this.controls) return;

      this.prevButton = this.controls.querySelector('[data-direction="prev"]');
      this.nextButton = this.controls.querySelector('[data-direction="next"]');

      // One signal removes every listener on disconnect, so a re-connect
      // can't stack a second set of handlers.
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.controls.querySelectorAll('[data-gridy-arrow]').forEach((button) => {
        button.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            if (!button.disabled) this.step(button.dataset.direction);
          },
          { signal }
        );
      });

      this.track.addEventListener('scroll', () => this.scheduleArrowEnds(), { passive: true, signal });

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.update();
    }

    observeVideos() {
      const videos = this.querySelectorAll('[data-highlights-video]');
      if (!videos.length || !('IntersectionObserver' in window)) return;

      // Starting playback is what makes a preload="none" video download.
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            if (isIntersecting) {
              target.play().catch(() => {});
            } else {
              target.pause();
            }
          });
        },
        { rootMargin: '200px' }
      );
      videos.forEach((video) => this.videoObserver.observe(video));
    }

    disconnectedCallback() {
      this.videoObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.abortController?.abort();
      cancelAnimationFrame(this.arrowFrame);
      this.arrowFrame = 0;
    }

    update() {
      const overflows = this.track.scrollWidth > this.track.clientWidth + 1;
      this.controls.toggleAttribute('hidden', !overflows);
      this.updateArrowEnds();
    }

    maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    /** Scroll by exactly one tile. Stops at either end. */
    step(direction) {
      const items = this.track.children;
      if (items.length < 2) return;

      const pitch = items[1].offsetLeft - items[0].offsetLeft;
      if (!pitch) return;

      const max = this.maxScroll();
      const current = this.track.scrollLeft;
      const target = direction === 'next' ? current + pitch : current - pitch;
      this.track.scrollTo({
        left: Math.max(0, Math.min(target, max)),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    /* Scroll fires many times a second; do the layout reads once per frame. */
    scheduleArrowEnds() {
      if (this.arrowFrame) return;
      this.arrowFrame = requestAnimationFrame(() => {
        this.arrowFrame = 0;
        this.updateArrowEnds();
      });
    }

    updateArrowEnds() {
      if (!this.prevButton || !this.nextButton) return;
      const max = this.maxScroll();
      const current = this.track.scrollLeft;
      this.prevButton.disabled = current <= 1;
      this.nextButton.disabled = max <= 1 || current >= max - 1;
    }
  }

  customElements.define('highlights-section', HighlightsSection);
})();
